import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Product } from '@/types';
import { addToCart, fetchCart } from '@/store/slices/cartSlice';
import { getPrimaryImage, getPlaceholderImage, getImageUrl, getAllImageUrls } from '@/utils/imageUtils';
import { formatPrice } from '@/utils/currencyUtils';
import { ShoppingCartIcon, StarIcon, ChevronLeftIcon, ChevronRightIcon, TruckIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { reviewsApi, ordersApi } from '@/services/api';
import toast from 'react-hot-toast';

interface ProductCardProps {
  product: Product;
  viewMode?: 'grid' | 'list';
  showAddToCart?: boolean;
  onProductClick?: (product: Product) => void;
  className?: string;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  viewMode = 'grid',
  showAddToCart = true,
  onProductClick,
  className = ''
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [ratingStats, setRatingStats] = useState<{ average_rating: number; total_reviews: number } | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const slideIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Get all product images - handle various formats safely
  let productImages: string[] = [getPlaceholderImage()];
  try {
    if (product.images) {
      productImages = getAllImageUrls(product.images);
    }
  } catch (error) {
    console.warn('Error processing product images:', error, product.images);
    productImages = [getPlaceholderImage()];
  }
  const hasMultipleImages = productImages.length > 1;

  // Fetch rating stats
  useEffect(() => {
    // Guard: Only fetch if product.id is a valid number
    if (!product?.id || typeof product.id !== 'number') {
      return;
    }

    const fetchRating = async () => {
      try {
        const response = await reviewsApi.getProductStats(product.id);
        if (response.data) {
          setRatingStats({
            average_rating: response.data.average_rating || 0,
            total_reviews: response.data.total_reviews || 0
          });
        }
      } catch (error: any) {
        // Silently fail - ratings are optional
        // If it's a 401, the interceptor will handle redirect
        // Don't show toast or log error for 401s or network errors
        if (error?.status_code !== 401 &&
            error?.response?.status !== 401 &&
            error?.code !== 'ERR_NETWORK' &&
            error?.message !== 'Network Error') {
          // Only log non-critical errors in debug mode
          if (process.env.NODE_ENV === 'development') {
            console.debug('Failed to fetch rating stats (non-critical):', error?.message || error);
          }
        }
      }
    };

    fetchRating();
  }, [product?.id]);


  const handleProductClick = () => {
    if (onProductClick) {
      onProductClick(product);
    } else {
      navigate(`/products/${product.id}`);
    }
  };

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      await dispatch(addToCart({ product_id: product.id, quantity: 1 })).unwrap();
      // Refresh cart to update the count in header
      dispatch(fetchCart());
      toast.success('Added to cart!');
    } catch (error: any) {
      // Don't show toast for 401 errors - interceptor will redirect to login
      if (error?.status_code !== 401 && error?.response?.status !== 401 && error !== 'Authentication required') {
        toast.error(error || 'Failed to add to cart');
      }
    }
  };

  const primaryImage = getPrimaryImage(product.images);
  const hasDiscount = product.original_price && product.original_price > product.price;

  // Calculate average rating for display
  const averageRating = ratingStats?.average_rating || 0;
  const totalReviews = ratingStats?.total_reviews || 0;

  // Get delivery info summary
  const getDeliveryInfo = () => {
    const deliveryOptions = product.delivery_options || [];
    if (deliveryOptions.length === 0) return null;

    const hasFreeDelivery = deliveryOptions.some(opt => opt.is_free_delivery);
    if (hasFreeDelivery) return { text: 'Free', isFree: true };

    const charges = deliveryOptions
      .filter(opt => !opt.is_free_delivery && opt.delivery_charge > 0)
      .map(opt => opt.delivery_charge);

    if (charges.length === 0) return { text: 'Free', isFree: true };
    if (charges.length === 1) return { text: `${charges[0]} tk`, isFree: false };

    const min = Math.min(...charges);
    const max = Math.max(...charges);
    return { text: `${min}-${max} tk`, isFree: false };
  };

  const deliveryInfo = getDeliveryInfo();
  
  // Render stars - Always show if rating exists
  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    return (
      <div className="flex items-center">
        {[...Array(fullStars)].map((_, i) => (
          <StarSolidIcon key={i} className="h-3.5 w-3.5 text-yellow-400" />
        ))}
        {hasHalfStar && (
          <div className="relative h-3.5 w-3.5">
            <StarIcon className="absolute h-3.5 w-3.5 text-gray-300" />
            <div className="absolute overflow-hidden h-3.5 w-1.75">
              <StarSolidIcon className="h-3.5 w-3.5 text-yellow-400" />
            </div>
          </div>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <StarIcon key={i} className="h-3.5 w-3.5 text-gray-300" />
        ))}
      </div>
    );
  };


  // Auto-slide images (only if multiple images)
  useEffect(() => {
    if (hasMultipleImages) {
      slideIntervalRef.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
      }, 3000); // Change image every 3 seconds
    }

    return () => {
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
      }
    };
  }, [hasMultipleImages, productImages.length]);

  const handlePreviousImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
      // Reset auto-slide timer
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
      }
      slideIntervalRef.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
      }, 3000);
    }
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
      // Reset auto-slide timer
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
      }
      slideIntervalRef.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
      }, 3000);
    }
  };

  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all cursor-pointer group ${
        viewMode === 'list' ? 'flex' : 'flex flex-col h-full'
      } ${className}`}
      onClick={handleProductClick}
    >
      {/* Image Section with Auto-Slider */}
      <div className={`relative ${viewMode === 'list' ? 'w-56 h-40 flex-shrink-0' : ''}`}>
        <div className={`relative overflow-hidden bg-gray-100 ${viewMode === 'list' ? 'w-full h-full' : 'w-full h-56'}`}>
          <img
            src={productImages[currentImageIndex]}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              // Only set placeholder if current src is not already a placeholder
              if (!target.src.includes('data:image/svg+xml') && !target.src.includes('placeholder')) {
                target.src = getPlaceholderImage(400, 400, 'No Image');
              }
            }}
          />
          
          {/* Navigation Buttons - Only show if multiple images */}
          {hasMultipleImages && (
            <>
              <button
                onClick={handlePreviousImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-1.5 rounded-full transition-all opacity-0 group-hover:opacity-100 z-10"
                aria-label="Previous image"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <button
                onClick={handleNextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-1.5 rounded-full transition-all opacity-0 group-hover:opacity-100 z-10"
                aria-label="Next image"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </>
          )}

          {/* Premium Badge */}
          {(product as any).is_premium && (
            <div className="absolute top-2 right-2 bg-gradient-to-r from-primary-400 to-primary-500 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg z-10 flex items-center space-x-1">
              <span>⭐</span>
              <span>Premium</span>
            </div>
          )}

          {/* Featured Badge (if not premium, show featured badge) */}
          {product.is_featured && !(product as any).is_premium && (
            <div className="absolute top-2 right-2 bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-lg z-10">
              Featured
            </div>
          )}
        </div>
      </div>

      {/* Content Section - Amazon Style */}
      <div className="px-4 pt-4 pb-4 flex flex-col flex-1">
        <div className="flex-grow">
          {/* Product Title */}
          <h3 className="text-sm text-gray-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors leading-tight">
            {product.name}
          </h3>

          {/* Rating - Always show if rating exists */}
          {averageRating > 0 && (
            <div className="flex items-center space-x-1 mb-2">
              {renderStars(averageRating)}
              <span className="text-xs text-primary-600 hover:text-primary-700 hover:underline">
                {averageRating.toFixed(1)}
              </span>
              {totalReviews > 0 && (
                <span className="text-xs text-gray-500">
                  ({totalReviews.toLocaleString()})
                </span>
              )}
            </div>
          )}

          {/* Product Description/Specifications */}
          {product.description && (
            <p className="text-xs text-gray-600 mb-2 line-clamp-2">
              {product.description}
            </p>
          )}

          {/* Price Section - Amazon Style */}
          <div className="mb-3">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="text-lg font-semibold text-gray-900 whitespace-nowrap">
                {formatPrice(product.price)}
              </span>
              {hasDiscount && (
                <span className="text-sm text-gray-500 line-through whitespace-nowrap">
                  {formatPrice(product.original_price)}
                </span>
              )}
            </div>
          </div>

          {/* Product Specifications */}
          <div className="text-xs text-gray-600 mb-3 space-y-1">
            {product.brand && (
              <p className="text-gray-700">
                <span className="font-medium">Brand:</span> {product.brand}
              </p>
            )}
            {product.model && (
              <p className="text-gray-700">
                <span className="font-medium">Model:</span> {product.model}
              </p>
            )}
            {product.condition && (
              <p className="text-gray-700">
                <span className="font-medium">Condition:</span> {product.condition}
              </p>
            )}
            {product.warranty && (
              <p className="text-gray-700">
                <span className="font-medium">Warranty:</span> {product.warranty}
              </p>
            )}
            {product.shop && (
              <p className="text-gray-500 mt-1.5">
                <span className="font-medium">Sold by:</span> {product.shop.name}
              </p>
            )}
            {product.inventory && (
              <p className={product.inventory.is_in_stock && product.inventory.current_stock > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                {product.inventory.is_in_stock && product.inventory.current_stock > 0 ? `✓ In Stock (${product.inventory.current_stock})` : '✗ Out of Stock'}
              </p>
            )}
          </div>

          {/* Delivery Information */}
          {deliveryInfo && (
            <div className="mb-2 pb-2 border-t pt-2">
              <div className="flex items-center text-xs">
                <TruckIcon className="h-4 w-4 mr-1.5 text-gray-500 flex-shrink-0" />
                <span className="text-gray-600">Delivery: </span>
                <span className={`ml-1 font-medium ${deliveryInfo.isFree ? 'text-green-600' : 'text-gray-900'}`}>
                  {deliveryInfo.text}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Add to Cart Button - Full Width, Prominent */}
        {showAddToCart && (
          <div className="pt-3 mt-auto">
            <button
              className="w-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
              onClick={handleAddToCart}
              disabled={product.inventory && (!product.inventory.is_in_stock || product.inventory.current_stock === 0)}
            >
              <ShoppingCartIcon className="h-4 w-4" />
              {product.inventory && (!product.inventory.is_in_stock || product.inventory.current_stock === 0) ? 'Out of Stock' : 'Add to Cart'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
